<script>
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import { vMaska } from 'maska/vue'

export default {
    name: 'PayWidget',

    directives: { maska: vMaska },

    setup() {
        const { locale } = useI18n()
        return { locale }
    },

    props: {
        transaction: {
            type: Object,
            required: true
        },
        apiBase: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            step: 'form',
            otpSent: false,
            phone: '',
            otp: '',
            loading: false,
            error: '',
            resultCode: null,
            resendTimer: 0,
            resendInterval: null
        }
    },

    computed: {
        phoneValid() {
            return this.phone.replace(/\D/g, '').length === 11
        },
        otpValid() {
            return this.otp.length >= 6
        },
        formattedAmount() {
            return Number(this.transaction.amount).toLocaleString('ru-RU')
        },
        availableLocales() {
            return ['ru', 'en']
        }
    },

    mounted() {
        if (this.transaction.language && this.availableLocales.includes(this.transaction.language)) {
            this.locale = this.transaction.language
        }
    },

    beforeUnmount() {
        clearInterval(this.resendInterval)
    },

    methods: {
        setLocale(lang) {
            this.locale = lang
        },

        async sendOtp() {
            if (!this.phoneValid || this.loading) return
            this.loading = true
            this.error = ''
            try {
                const res = await axios.post(`${this.apiBase}/w-create`, {
                    msisdn: this.phone,
                    gtid: this.transaction.gtid,
                })
                if (!res.data.success) {
                    this.error = this.$t('status_' + res.data.resultCode, this.$t('statusFallback'))
                    return
                }
                this.otpSent = true
                this.startResendTimer()
            } catch (error) {
                const code = error.response?.data?.resultCode
                this.error = this.$t('status_' + code, this.$t('statusFallback'))
            } finally {
                this.loading = false
            }
        },

        async verifyOtp() {
            if (!this.otpValid || this.loading) return
            this.loading = true
            this.error = ''
            try {
                const res = await axios.post(`${this.apiBase}/w-confirm`, {
                    gtid: this.transaction.gtid,
                    pin: this.otp,
                })
                if (!res.data.success) {
                    this.error = this.$t('status_' + res.data.resultCode, this.$t('statusFallback'))
                    this.loading = false
                    return
                }
                this.resultCode = res.data.resultCode
                this.step = 'success'
                window.parent.postMessage({ type: 'PAYMENT_SUCCESS', gtid: this.transaction.gtid }, '*')

                    //listner
                // window.addEventListener('message', (event) => {
                //     if (event.data.type === 'PAYMENT_SUCCESS') {
                //
                //     }
                // })

                // setTimeout(() => {
                //     window.location.href = this.transaction.successUrl
                // }, 5000)
            } catch (error) {
                const code = error.response?.data?.resultCode
                this.error = this.$t('status_' + code, this.$t('statusFallback'))
                this.loading = false
            }
        },

        async resendOtp() {
            if (!this.phoneValid || this.loading) return
            this.loading = true
            this.error = ''
            try {
                const res = await axios.post(`${this.apiBase}/w-resend-pin`, {
                    gtid: this.transaction.gtid,
                })
                if (!res.data.success) {
                    this.error = this.$t('status_' + res.data.resultCode, this.$t('statusFallback'))
                    return
                }
                this.otpSent = true
                this.startResendTimer()
            } catch (error) {
                const code = error.response?.data?.resultCode
                this.error = this.$t('status_' + code, this.$t('statusFallback'))
            } finally {
                this.loading = false
            }
        },

        resetPhone() {
            this.otpSent = false
            this.otp = ''
            this.error = ''
            clearInterval(this.resendInterval)
            this.resendTimer = 0
        },

        startResendTimer() {
            this.resendTimer = 60;
            clearInterval(this.resendInterval);
            this.resendInterval = setInterval(() => {
                if (this.resendTimer <= 0) return clearInterval(this.resendInterval)
                this.resendTimer--
            }, 1000)
        }
    }
}
</script>

<template>
    <div class="pay-widget font-sans w-full">
        <!-- Header -->
        <div class="flex w-full flex-col items-center">
            <div class="flex w-full p-4 bg-[#073938] justify-end gap-2">
                <button
                    v-for="lang in availableLocales"
                    :key="lang"
                    class="text-xs transition"
                    :class="locale === lang ? 'text-white font-semibold' : 'text-white/40 hover:text-white/70'"
                    @click="setLocale(lang)"
                >
                    {{ lang.toUpperCase() }}
                </button>
            </div>
            <p class="text-3xl text-white font-bold mt-4">tele<span class="text-2xl font-normal">.pay</span></p>
        </div>

        <!-- Transaction info -->
        <div class="px-6 pb-6">
<!--            <p class="text-xs text-white uppercase tracking-wide mb-1">{{ $t('toPay') }}</p>-->
            <p class="text-2xl font-bold text-white">
                {{ formattedAmount }} <span class="text-lg font-medium">{{ transaction.currency }}</span>
            </p>
            <p v-if="transaction.description" class="text-sm text-white mt-1">{{ transaction.description }}</p>
            <hr class="w-full h-[1px] bg-gray-100 mt-6">
        </div>

        <!-- Success -->
        <Transition name="fade" mode="out-in">
            <div v-if="step === 'success'" class="text-center space-y-3 py-4 px-6">
                <div class="flex justify-center">
                    <div class="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                        <svg class="w-7 h-7 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
                        </svg>
                    </div>
                </div>
                <p class="font-medium text-white">{{ $t('paymentConfirmed') }}</p>
                <p v-if="resultCode" class="text-sm text-gray-300">{{ $t('status_' + resultCode, $t('statusFallback')) }}</p>
<!--                <p class="text-sm text-white">{{ $t('redirecting') }}</p>-->
            </div>

            <!-- Form -->
            <div v-else class="space-y-4 px-6 pb-[30px]">

                <!-- Phone -->
                <div>
                    <label class="block text-[12px] font-medium text-white mb-2">{{ $t('phone') }}</label>
                    <div
                        class="w-full relative">
                        <input
                            v-model="phone"
                            v-maska="'+31 ## ### ####'"
                            type="tel"
                            placeholder="+31 __ ___ ____"
                            :disabled="loading || otpSent"
                            class="w-full pl-[48px] px-4 py-2.5 border-[1px] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition disabled:opacity-50 bg-transparent text-white"
                            @keyup.enter="sendOtp"
                        />

                        <img src="/img/flag-400.png" alt="" class="max-w-[26px] absolute left-4 top-1/2 -translate-y-1/2">
                    </div>
                </div>

                <!-- OTP -->
                <Transition name="slide-down">
                    <div v-if="otpSent" class="space-y-4">
                        <div>
                            <div class="flex items-center justify-between mb-2">
                                <label class="text-[12px] font-medium text-white">{{ $t('smsCode') }}</label>
                                <button class="text-xs text-white underline" @click="resetPhone">
                                    {{ $t('changePhone') }}
                                </button>
                            </div>
                            <input
                                v-model="otp"
                                type="text"
                                inputmode="numeric"
                                maxlength="6"
                                :disabled="loading"
                                class="w-full px-4 py-2.5 border-[1px] rounded-lg text-sm text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition disabled:opacity-50 bg-transparent text-white"
                                @keyup.enter="verifyOtp"
                            />
                        </div>
                        <p v-if="resendTimer > 0" class="text-xs text-center text-gray-300">
                            {{ $t('resendAfter', { n: resendTimer }) }}
                        </p>
                        <p
                            v-else
                            class="w-full text-xs font-semibold text-white text-center">
                            {{ $t('resendText') }}
                            <span
                                @click="resendOtp"
                                class="text-yellow-500 hover:underline hover:cursor-pointer">
                                {{ $t('resendLink') }}
                            </span>
                        </p>

                    </div>
                </Transition>

                <p v-if="error" class="text-red-500 text-xs text-center">{{ error }}</p>

                <!-- Primary button -->
                <button
                    v-if="!otpSent"
                    :disabled="!phoneValid || loading"
                    class="w-full py-2.5 px-4 bg-[#073e3b] border-[1px] text-white text-sm font-medium rounded-lg hover:bg-[#073e3b70] active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    @click="sendOtp"
                >
                    <span v-if="loading" class="inline-flex items-center justify-center gap-2">
                        <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                        </svg>
                        {{ $t('sending') }}
                    </span>
                    <span v-else>{{ $t('sendCode') }}</span>
                </button>
                <button
                    v-else
                    :disabled="!otpValid || loading"
                    class="w-full py-2.5 px-4 bg-[#073e3b] border-[1px] text-white text-sm font-medium rounded-lg hover:bg-[#073e3b70] active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    @click="verifyOtp"
                >
                    <span v-if="loading" class="inline-flex items-center justify-center gap-2">
                        <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                        </svg>
                        {{ $t('confirming') }}
                    </span>
                    <span v-else>{{ $t('confirm') }}</span>
                </button>

            </div>
        </Transition>
    </div>
</template>

<style>
@import '../assets/tailwind.css';

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-enter-from {
    opacity: 0;
    transform: translateX(10px);
}
.fade-leave-to {
    opacity: 0;
    transform: translateX(-10px);
}

.slide-down-enter-active {
    transition: opacity 0.25s ease, transform 0.25s ease;
}
.slide-down-enter-from {
    opacity: 0;
    transform: translateY(-6px);
}
</style>
